import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      'app.copyright': 'All processing is done locally, protecting your data privacy',
      'app.title': 'Image Processor',
      'header.title': 'Image Processor',
      'header.subtitle': 'Simple, fast image processing',

      'lang.en': 'English',
      'lang.zh': '中文',
      'switch.label': 'Language',
      'aria.switch.zh': 'Switch to Chinese',
      'aria.switch.en': 'Switch to English',

      'common.processing': 'Processing...',

      'tool.selector.upload.tooltip': 'Upload image',
      'tool.selector.upload.aria': 'Upload image tool',
      'tool.selector.scale.tooltip': 'Scale image',
      'tool.selector.scale.aria': 'Image scaling tool',
      'tool.selector.crop.tooltip': 'Crop image',
      'tool.selector.crop.aria': 'Image cropping tool',
      'tool.selector.format.tooltip': 'Convert format',
      'tool.selector.format.aria': 'Image format conversion tool',
      'tool.selector.grid.tooltip': 'Grid stitch',
      'tool.selector.grid.aria': 'Grid stitch tool',

      'tool.upload.title': 'Upload Image',
      'tool.upload.subtitle': 'Click or drag images here to upload. JPG, PNG, WebP and other common formats are supported.',
      'tool.upload.selectFile': 'Choose image file',
      'tool.upload.supportedFormats': 'Supported formats: JPG, PNG, WebP, GIF',

      'scale.title': 'Scale Settings',
      'scale.tooltip': 'Adjust image size',
      'scale.decrease.aria': 'Zoom out image',
      'scale.increase.aria': 'Zoom in image',
      'scale.ratioLabel': 'Scale ratio',
      'scale.slider.aria': 'Scale slider',
      'scale.apply': 'Apply Scale',

      'crop.title': 'Crop Settings',
      'crop.livePreview': 'Live Preview',
      'crop.tooltip': 'Drag the slider to preview the crop in real time',
      'crop.width.label': 'Width (%)',
      'crop.width.aria': 'Crop width',
      'crop.width.numberAria': 'Width value',
      'crop.width.maxHint': 'Max crop 100%',
      'crop.height.label': 'Height (%)',
      'crop.height.aria': 'Crop height',
      'crop.height.numberAria': 'Height value',
      'crop.height.maxHint': 'Max crop 100%',
      'crop.x.label': 'X Offset (%)',
      'crop.x.aria': 'X offset',
      'crop.x.numberAria': 'X offset value',
      'crop.x.maxHint': 'Max {{value}}%',
      'crop.y.label': 'Y Offset (%)',
      'crop.y.aria': 'Y offset',
      'crop.y.numberAria': 'Y offset value',
      'crop.y.maxHint': 'Max {{value}}%',
      'crop.confirm': 'Confirm Crop',

      'format.title': 'Format Conversion',
      'format.tooltip': 'Convert image format',
      'format.chooseFormat': 'Choose output format:',
      'format.png.title': 'PNG',
      'format.png.desc': 'Supports transparent background',
      'format.jpeg.title': 'JPEG',
      'format.jpeg.desc': 'Smaller file size',
      'format.webp.title': 'WebP',
      'format.webp.desc': 'Modern efficient format',
      'format.quality.label': 'Image quality ({{value}}%)',
      'format.quality.aria': 'Image quality',
      'format.convert': 'Convert Format',

      'grid.title': 'Grid Stitch',
      'grid.tooltip': 'Stitch multiple images into a grid',
      'grid.chooseLayout': 'Choose grid layout:',
      'grid.layout.suffix': ' grid',
      'grid.rows': 'Rows',
      'grid.rows.aria': 'Grid rows',
      'grid.cols': 'Columns',
      'grid.cols.aria': 'Grid columns',
      'grid.advanced.title': 'Advanced Settings',
      'grid.fitMode.label': 'Image fit mode',
      'grid.fitMode.cover.title': 'Cover',
      'grid.fitMode.cover.desc': 'Fill the cell, edges may be cropped',
      'grid.fitMode.contain.title': 'Contain',
      'grid.fitMode.contain.desc': 'Show the whole image, margins may appear',
      'grid.spacing.label': 'Spacing ({{value}}px)',
      'grid.spacing.aria': 'Grid spacing',
      'grid.canvasSize.label': 'Canvas size ({{value}}px)',
      'grid.canvasSize.aria': 'Canvas size',
      'grid.canvasSize.small': 'Small (400px)',
      'grid.canvasSize.medium': 'Medium (800px)',
      'grid.canvasSize.large': 'Large (1600px)',
      'grid.quality.label': 'Image quality ({{value}}%)',
      'grid.quality.aria': 'Grid image quality',
      'grid.addImages.label': 'Add images (up to {{count}}):',
      'grid.image.alt': 'Grid image {{index}}',
      'grid.removeImage.aria': 'Remove image {{index}}',
      'grid.addImage.button': 'Add image',
      'grid.generating': 'Generating...',
      'grid.generate': 'Generate Grid Image',
      'grid.layout.hetero1': 'Top 2, Bottom 1',
      'grid.layout.hetero2': 'Left 2, Right 1',
      'grid.layout.hetero3': 'Top 1, Bottom 2',
      'grid.layout.hetero4': 'Left 1, Right 2',

      'preview.title.grid': 'Grid Preview',
      'preview.title.image': 'Image Preview',
      'preview.downloadable': 'Downloadable',
      'preview.empty': 'The image will be previewed here',
      'preview.clear': 'Clear Image',
      'preview.download': 'Download Image'
    }
  },
  zh: {
    translation: {
      'app.copyright': '所有处理均在本地完成，保护你的隐私',
      'app.title': '图片处理工具',
      'header.title': '图片处理工具',
      'header.subtitle': '简单、快速地处理你的图片',

      'lang.en': 'English',
      'lang.zh': '中文',
      'switch.label': '语言',
      'aria.switch.zh': '切换到中文',
      'aria.switch.en': '切换到英文',

      'common.processing': '处理中...',

      'tool.selector.upload.tooltip': '上传图片',
      'tool.selector.upload.aria': '上传图片工具',
      'tool.selector.scale.tooltip': '缩放图片',
      'tool.selector.scale.aria': '图片缩放工具',
      'tool.selector.crop.tooltip': '裁剪图片',
      'tool.selector.crop.aria': '图片裁剪工具',
      'tool.selector.format.tooltip': '转换格式',
      'tool.selector.format.aria': '图片格式转换工具',
      'tool.selector.grid.tooltip': '宫格拼接',
      'tool.selector.grid.aria': '宫格拼接工具',

      'tool.upload.title': '上传图片',
      'tool.upload.subtitle': '点击或拖拽图片到此处上传，支持JPG、PNG、WebP等常见格式',
      'tool.upload.selectFile': '选择图片文件',
      'tool.upload.supportedFormats': '支持的格式: JPG, PNG, WebP, GIF',

      'scale.title': '缩放设置',
      'scale.tooltip': '调整图片大小',
      'scale.decrease.aria': '缩小图片',
      'scale.increase.aria': '放大图片',
      'scale.ratioLabel': '缩放比例',
      'scale.slider.aria': '缩放滑块',
      'scale.apply': '应用缩放',

      'crop.title': '裁剪设置',
      'crop.livePreview': '实时预览',
      'crop.tooltip': '拖动滑块实时预览裁剪效果',
      'crop.width.label': '宽度 (%)',
      'crop.width.aria': '裁剪宽度',
      'crop.width.numberAria': '宽度数值',
      'crop.width.maxHint': '最大可裁剪100%',
      'crop.height.label': '高度 (%)',
      'crop.height.aria': '裁剪高度',
      'crop.height.numberAria': '高度数值',
      'crop.height.maxHint': '最大可裁剪100%',
      'crop.x.label': 'X 偏移 (%)',
      'crop.x.aria': 'X轴偏移',
      'crop.x.numberAria': 'X偏移数值',
      'crop.x.maxHint': '最大{{value}}%',
      'crop.y.label': 'Y 偏移 (%)',
      'crop.y.aria': 'Y轴偏移',
      'crop.y.numberAria': 'Y偏移数值',
      'crop.y.maxHint': '最大{{value}}%',
      'crop.confirm': '确认裁剪',

      'format.title': '格式转换',
      'format.tooltip': '转换图片格式',
      'format.chooseFormat': '选择输出格式：',
      'format.png.title': 'PNG',
      'format.png.desc': '支持透明背景',
      'format.jpeg.title': 'JPEG',
      'format.jpeg.desc': '较小文件体积',
      'format.webp.title': 'WebP',
      'format.webp.desc': '现代高效格式',
      'format.quality.label': '图片质量 ({{value}}%)',
      'format.quality.aria': '图片质量',
      'format.convert': '转换格式',

      'grid.title': '宫格拼接',
      'grid.tooltip': '将多张图片拼接成宫格',
      'grid.chooseLayout': '选择宫格布局：',
      'grid.layout.suffix': '宫格',
      'grid.rows': '行数',
      'grid.rows.aria': '宫格行数',
      'grid.cols': '列数',
      'grid.cols.aria': '宫格列数',
      'grid.advanced.title': '高级设置',
      'grid.fitMode.label': '图片适应模式',
      'grid.fitMode.cover.title': '填充模式',
      'grid.fitMode.cover.desc': '图片填满单元格，可能裁剪边缘',
      'grid.fitMode.contain.title': '适应模式',
      'grid.fitMode.contain.desc': '完整显示图片，可能有边距',
      'grid.spacing.label': '宫格间距 ({{value}}px)',
      'grid.spacing.aria': '宫格间距',
      'grid.canvasSize.label': '画布大小 ({{value}}px)',
      'grid.canvasSize.aria': '画布大小',
      'grid.canvasSize.small': '小 (400px)',
      'grid.canvasSize.medium': '中 (800px)',
      'grid.canvasSize.large': '大 (1600px)',
      'grid.quality.label': '图片质量 ({{value}}%)',
      'grid.quality.aria': '宫格图片质量',
      'grid.addImages.label': '添加图片（最多 {{count}} 张）：',
      'grid.image.alt': '宫格图片 {{index}}',
      'grid.removeImage.aria': '移除第{{index}}张图片',
      'grid.addImage.button': '添加图片',
      'grid.generating': '生成中...',
      'grid.generate': '生成宫格图片',
      'grid.layout.hetero1': '上2下1',
      'grid.layout.hetero2': '左2右1',
      'grid.layout.hetero3': '上1下2',
      'grid.layout.hetero4': '左1右2',

      'preview.title.grid': '宫格预览',
      'preview.title.image': '图片预览',
      'preview.downloadable': '可下载',
      'preview.empty': '图片将在这里预览',
      'preview.clear': '清空图片',
      'preview.download': '下载图片'
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng'
    },
    interpolation: { escapeValue: false }
  });

export default i18n;